import React, { useState, useEffect } from 'react';
import { ScheduleEvent, ScheduleEventCreate, ScheduleEventUpdate } from '../../../types';
import { scheduleEventService } from '../../../services/scheduleEventService';
import Modal from '../../shared/Modal';

const COLOR_PRESETS = [
  { label: 'Green', value: '#2c5530' },
  { label: 'Orange', value: '#f39c12' },
  { label: 'Red', value: '#DC2626' },
  { label: 'Blue', value: '#3B82F6' },
  { label: 'Purple', value: '#8B5CF6' },
];

const ScheduleEventsCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [events, setEvents] = useState<ScheduleEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<ScheduleEvent | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    color: '#2c5530',
    active: true,
  });
  const [saving, setSaving] = useState(false);

  // Add modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    color: '#2c5530',
    active: true,
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await scheduleEventService.getScheduleEvents();
      setEvents(data);
      setError(null);
    } catch (err) {
      setError('Failed to load schedule events');
      console.error('Error loading schedule events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (eventId: number) => {
    if (!window.confirm('Are you sure you want to delete this schedule event?')) {
      return;
    }
    try {
      await scheduleEventService.deleteScheduleEvent(eventId);
      await loadEvents();
    } catch (err) {
      console.error('Error deleting schedule event:', err);
      alert('Failed to delete event. Please try again.');
    }
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateForInput = (dateStr: string): string => {
    return dateStr.split('T')[0];
  };

  const handleEdit = (event: ScheduleEvent) => {
    setEditingEvent(event);
    setEditForm({
      name: event.name,
      start_date: formatDateForInput(event.start_date),
      end_date: formatDateForInput(event.end_date),
      color: event.color,
      active: event.active,
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingEvent) return;

    if (!editForm.name.trim()) {
      alert('Name is required');
      return;
    }
    if (!editForm.start_date || !editForm.end_date) {
      alert('Start date and end date are required');
      return;
    }
    if (new Date(editForm.start_date) > new Date(editForm.end_date)) {
      alert('Start date must be on or before end date');
      return;
    }

    try {
      setSaving(true);
      const updateData: ScheduleEventUpdate = {
        name: editForm.name.trim(),
        start_date: editForm.start_date,
        end_date: editForm.end_date,
        color: editForm.color,
        active: editForm.active,
      };
      await scheduleEventService.updateScheduleEvent(editingEvent.id, updateData);
      await loadEvents();
      setEditModalOpen(false);
      setEditingEvent(null);
    } catch (err) {
      console.error('Error updating schedule event:', err);
      alert('Failed to update event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingEvent(null);
  };

  const handleOpenAddModal = () => {
    setAddForm({
      name: '',
      start_date: '',
      end_date: '',
      color: '#2c5530',
      active: true,
    });
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleSaveAdd = async () => {
    if (!addForm.name.trim()) {
      alert('Name is required');
      return;
    }
    if (!addForm.start_date || !addForm.end_date) {
      alert('Start date and end date are required');
      return;
    }
    if (new Date(addForm.start_date) > new Date(addForm.end_date)) {
      alert('Start date must be on or before end date');
      return;
    }

    try {
      setSaving(true);
      const createData: ScheduleEventCreate = {
        name: addForm.name.trim(),
        start_date: addForm.start_date,
        end_date: addForm.end_date,
        color: addForm.color,
        active: addForm.active,
      };
      await scheduleEventService.createScheduleEvent(createData);
      await loadEvents();
      setAddModalOpen(false);
    } catch (err) {
      console.error('Error creating schedule event:', err);
      alert('Failed to create event. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderColorPicker = (
    value: string,
    onChange: (color: string) => void
  ) => (
    <div className="color-picker-row">
      {COLOR_PRESETS.map((preset) => (
        <button
          key={preset.value}
          type="button"
          className={`color-swatch-btn ${value === preset.value ? 'selected' : ''}`}
          style={{ backgroundColor: preset.value }}
          onClick={() => onChange(preset.value)}
          title={preset.label}
        />
      ))}
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="color-input-custom"
        title="Custom color"
      />
    </div>
  );

  const renderFormFields = (
    form: typeof addForm,
    setForm: React.Dispatch<React.SetStateAction<typeof addForm>>,
    prefix: string
  ) => (
    <div className="edit-form">
      <div className="form-group">
        <label htmlFor={`${prefix}-name`}>Name *</label>
        <input
          id={`${prefix}-name`}
          type="text"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="e.g. July 4th Weekend, Labor Day Weekend"
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${prefix}-start`}>Start Date *</label>
        <input
          id={`${prefix}-start`}
          type="date"
          value={form.start_date}
          onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.target.value }))}
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${prefix}-end`}>End Date *</label>
        <input
          id={`${prefix}-end`}
          type="date"
          value={form.end_date}
          onChange={(e) => setForm((prev) => ({ ...prev, end_date: e.target.value }))}
        />
      </div>
      <div className="form-group">
        <label>Color</label>
        {renderColorPicker(form.color, (color) =>
          setForm((prev) => ({ ...prev, color }))
        )}
      </div>
      <div className="form-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
          />
          Active
        </label>
        <small className="form-help">
          Only active events are shown on the reservation schedule.
        </small>
      </div>
    </div>
  );

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">🗓️</span>
          <h2>Schedule Event Display Control</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">schedule_events</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          {loading ? (
            <div className="config-loading">Loading schedule events...</div>
          ) : error ? (
            <div className="config-error">{error}</div>
          ) : events.length === 0 ? (
            <div className="config-empty">
              <p>No schedule events configured. Add holidays and events to display on the reservation schedule.</p>
              <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>
                + Add Schedule Event
              </button>
            </div>
          ) : (
            <>
              <table className="config-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Color</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td>{event.name}</td>
                      <td>{formatDate(event.start_date)}</td>
                      <td>{formatDate(event.end_date)}</td>
                      <td>
                        <span
                          className="color-swatch-display"
                          style={{ backgroundColor: event.color }}
                        />
                      </td>
                      <td>
                        {event.active ? (
                          <span className="status-active">Active</span>
                        ) : (
                          <span className="status-inactive">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="config-actions">
                          <button
                            className="btn btn-text btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(event);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-text btn-sm btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(event.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="config-card-footer">
                <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>
                  + Add Schedule Event
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        title="Edit Schedule Event"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseEditModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        {renderFormFields(editForm, setEditForm, 'edit-event')}
      </Modal>

      {/* Add Modal */}
      <Modal
        isOpen={addModalOpen}
        onClose={handleCloseAddModal}
        title="Add Schedule Event"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseAddModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveAdd} disabled={saving}>
              {saving ? 'Adding...' : 'Add Event'}
            </button>
          </>
        }
      >
        {renderFormFields(addForm, setAddForm, 'add-event')}
      </Modal>
    </div>
  );
};

export default ScheduleEventsCard;
